import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal, Field } from "./Rooms";
import { Plus, Pencil, Trash2, ShieldCheck, Users as UsersIcon, Lock } from "lucide-react";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

// Human-friendly module labels (falls back to the raw key when a t() miss occurs).
const MODULE_LABEL = {
    dashboard: "Dashboard",
    properties: "Properties",
    rooms: "Rooms",
    reservations: "Reservations",
    guests: "Guests",
    staff: "Staff",
    housekeeping: "Housekeeping",
    pos: "POS",
    channels: "Channels",
    reports: "Reports",
    audit: "Audit",
    settings: "Settings",
    billing: "Billing",
};

export default function Staff() {
    const { t } = useTranslation();
    const [tab, setTab] = useState("users");
    const [users, setUsers] = useState([]);
    const [rolesData, setRolesData] = useState({ modules: [], roles: [] });
    const [openUser, setOpenUser] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [openRole, setOpenRole] = useState(false);
    const [editRole, setEditRole] = useState(null);

    const loadUsers = async () => {
        const { data } = await api.get("/users");
        setUsers(data);
    };
    const loadRoles = async () => {
        const { data } = await api.get("/roles");
        setRolesData(data);
    };
    const loadAll = async () => { await Promise.all([loadUsers(), loadRoles()]); };
    useEffect(() => { loadAll(); }, []);

    const roleByKey = useMemo(() => {
        const m = {};
        (rolesData.roles || []).forEach((r) => { m[r.key] = r; });
        return m;
    }, [rolesData]);

    const toggleActive = async (u) => {
        try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); await loadUsers(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const removeUser = async (u) => {
        if (!window.confirm(t("staff.delete_confirm", { defaultValue: `Fshi përdoruesin ${u.name}?` }))) return;
        try { await api.delete(`/users/${u.id}`); toast.success(t("staff.deleted", { defaultValue: "Përdoruesi u fshi" })); await loadUsers(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const removeRole = async (r) => {
        if (!window.confirm(t("staff.role_delete_confirm", { defaultValue: `Fshi rolin ${r.name}?` }))) return;
        try { await api.delete(`/roles/${r.id}`); toast.success(t("staff.role_deleted", { defaultValue: "Roli u fshi" })); await loadRoles(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const totalRoles = (rolesData.roles || []).length;
    const customRoles = (rolesData.roles || []).filter((r) => !r.is_builtin).length;

    return (
        <>
            <Topbar
                title={t("sidebar.staff", { defaultValue: "Stafi" })}
                subtitle={tab === "users"
                    ? t("staff.subtitle_users", { count: users.length, defaultValue: `${users.length} përdorues` })
                    : t("staff.subtitle_roles", { total: totalRoles, custom: customRoles, defaultValue: `${totalRoles} role · ${customRoles} të personalizuara` })}
                actions={
                    tab === "users" ? (
                        <button onClick={() => { setEditUser(null); setOpenUser(true); }} className="akm-btn-primary" data-testid="new-staff-btn">
                            <Plus size={14} /> {t("staff.add_user", { defaultValue: "Shto përdorues" })}
                        </button>
                    ) : (
                        <button onClick={() => { setEditRole(null); setOpenRole(true); }} className="akm-btn-primary" data-testid="new-role-btn">
                            <Plus size={14} /> {t("staff.add_role", { defaultValue: "Shto rol" })}
                        </button>
                    )
                }
            />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                {/* Tabs */}
                <div className="mb-4 flex items-center gap-1 border-b border-border">
                    <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={<UsersIcon size={14} />} testid="tab-users">
                        {t("staff.tab_users", { defaultValue: "Përdoruesit" })}
                    </TabButton>
                    <TabButton active={tab === "roles"} onClick={() => setTab("roles")} icon={<ShieldCheck size={14} />} testid="tab-roles">
                        {t("staff.tab_roles", { defaultValue: "Rolet dhe privilegjet" })}
                    </TabButton>
                </div>

                {tab === "users" && (
                    <div className="akm-card overflow-hidden">
                        <table className="akm-table w-full">
                            <thead className="bg-muted/40">
                                <tr>
                                    <th>{t("common.name", { defaultValue: "Emri" })}</th>
                                    <th>{t("common.email", { defaultValue: "Email" })}</th>
                                    <th>{t("staff.role", { defaultValue: "Roli" })}</th>
                                    <th>{t("common.status", { defaultValue: "Statusi" })}</th>
                                    <th>{t("staff.added", { defaultValue: "Shtuar" })}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => {
                                    const roleDisp = roleByKey[u.role]?.name || (u.role || "").replace(/_/g, " ");
                                    return (
                                        <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                            <td className="font-medium">{u.name}</td>
                                            <td className="font-mono text-[12px] text-muted-foreground">{u.email}</td>
                                            <td>
                                                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground capitalize">
                                                    {roleDisp}
                                                </span>
                                            </td>
                                            <td><StatusBadge status={u.is_active ? "active" : "skipped"} label={u.is_active ? t("common.active", { defaultValue: "Aktiv" }) : t("common.disabled", { defaultValue: "Çaktivizuar" })} /></td>
                                            <td className="text-muted-foreground">{formatDate(u.created_at)}</td>
                                            <td className="text-right">
                                                <div className="flex justify-end gap-3">
                                                    <button onClick={() => { setEditUser(u); setOpenUser(true); }} className="text-[11px] text-muted-foreground hover:text-foreground" title={t("common.edit", { defaultValue: "Edito" })} data-testid={`user-edit-${u.id}`}>
                                                        <Pencil size={13} />
                                                    </button>
                                                    <button onClick={() => toggleActive(u)} className="text-[11px] text-muted-foreground hover:underline" data-testid={`user-toggle-${u.id}`}>
                                                        {u.is_active ? t("common.disable", { defaultValue: "Çaktivizo" }) : t("common.enable", { defaultValue: "Aktivizo" })}
                                                    </button>
                                                    <button onClick={() => removeUser(u)} className="text-[11px] text-red-600 hover:text-red-700" title={t("common.delete", { defaultValue: "Fshi" })} data-testid={`user-delete-${u.id}`}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {users.length === 0 && (
                                    <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{t("staff.empty_users", { defaultValue: "Asnjë përdorues" })}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "roles" && (
                    <div className="akm-card overflow-hidden">
                        <table className="akm-table w-full">
                            <thead className="bg-muted/40">
                                <tr>
                                    <th>{t("staff.role_name", { defaultValue: "Roli" })}</th>
                                    <th>{t("staff.role_key", { defaultValue: "Çelësi" })}</th>
                                    <th>{t("staff.role_modules", { defaultValue: "Modulet e lejuara" })}</th>
                                    <th>{t("staff.role_type", { defaultValue: "Lloji" })}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(rolesData.roles || []).map((r) => (
                                    <tr key={r.id} data-testid={`role-row-${r.key}`}>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="font-mono text-[11px] text-muted-foreground">{r.key}</td>
                                        <td>
                                            <div className="flex flex-wrap gap-1">
                                                {r.modules.map((m) => (
                                                    <span key={m} className="rounded-md bg-muted px-1.5 py-0.5 text-[10.5px] font-medium text-foreground">
                                                        {t(`staff.module.${m}`, { defaultValue: MODULE_LABEL[m] || m })}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            {r.is_builtin ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                                                    <Lock size={10} /> {t("staff.builtin", { defaultValue: "Sistem" })}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-800">
                                                    {t("staff.custom", { defaultValue: "Personal" })}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            {r.is_builtin ? (
                                                <span className="text-[11px] text-muted-foreground">—</span>
                                            ) : (
                                                <div className="flex justify-end gap-3">
                                                    <button onClick={() => { setEditRole(r); setOpenRole(true); }} className="text-[11px] text-muted-foreground hover:text-foreground" title={t("common.edit", { defaultValue: "Edito" })} data-testid={`role-edit-${r.key}`}>
                                                        <Pencil size={13} />
                                                    </button>
                                                    <button onClick={() => removeRole(r)} className="text-[11px] text-red-600 hover:text-red-700" title={t("common.delete", { defaultValue: "Fshi" })} data-testid={`role-delete-${r.key}`}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {openUser && (
                <StaffForm
                    initial={editUser}
                    roles={rolesData.roles || []}
                    onClose={() => { setOpenUser(false); setEditUser(null); }}
                    onSaved={async () => { setOpenUser(false); setEditUser(null); await loadUsers(); }}
                />
            )}
            {openRole && (
                <RoleForm
                    initial={editRole}
                    modules={rolesData.modules || []}
                    onClose={() => { setOpenRole(false); setEditRole(null); }}
                    onSaved={async () => { setOpenRole(false); setEditRole(null); await loadRoles(); }}
                />
            )}
        </>
    );
}

function TabButton({ active, onClick, icon, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition ${active
                ? "border-[#ff7b02] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
            {icon}
            {children}
        </button>
    );
}

function StaffForm({ initial, roles, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = !!initial;
    const [form, setForm] = useState({
        name: initial?.name || "",
        email: initial?.email || "",
        password: "",
        role: initial?.role || "reception",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/users/${initial.id}`, { name: form.name, role: form.role });
                toast.success(t("staff.updated", { defaultValue: "Përdoruesi u përditësua" }));
            } else {
                await api.post("/auth/users", form);
                toast.success(t("staff.created", { defaultValue: "Përdoruesi u krijua" }));
            }
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <Modal title={isEdit ? t("staff.edit_user", { defaultValue: "Edito përdoruesin" }) : t("staff.add_user", { defaultValue: "Shto përdorues" })} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label={t("common.name", { defaultValue: "Emri" })}>
                    <input className="akm-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
                </Field>
                <Field label={t("common.email", { defaultValue: "Email" })}>
                    <input type="email" className="akm-input" required value={form.email} disabled={isEdit} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
                </Field>
                {!isEdit && (
                    <Field label={t("common.password", { defaultValue: "Fjalëkalimi" })}>
                        <input type="password" className="akm-input" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
                    </Field>
                )}
                <Field label={t("staff.role", { defaultValue: "Roli" })}>
                    <select className="akm-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
                        {roles.filter((r) => r.key !== "super_admin").map((r) => (
                            <option key={r.key} value={r.key}>
                                {r.is_builtin ? r.name : `${r.name} · ${t("staff.custom", { defaultValue: "Personal" })}`}
                            </option>
                        ))}
                    </select>
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel", { defaultValue: "Anulo" })}</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="staff-save">
                        {saving ? t("common.saving", { defaultValue: "Duke ruajtur…" }) : t("common.save", { defaultValue: "Ruaj" })}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function RoleForm({ initial, modules, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = !!initial;
    const [form, setForm] = useState({
        name: initial?.name || "",
        description: initial?.description || "",
        modules: new Set(initial?.modules || ["dashboard"]),
        permissions: new Set(initial?.permissions || []),
    });
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("modules"); // "modules" | "permissions"
    const [catalogue, setCatalogue] = useState([]); // [{category, permissions:[{key,label_sq,sensitive}]}]
    const [permSearch, setPermSearch] = useState("");

    // Load the permission catalogue once on mount for the fine-grained matrix.
    useEffect(() => {
        api.get("/roles/catalogue").then(({ data }) => setCatalogue(data.categories || []))
            .catch(() => setCatalogue([]));
    }, []);

    const toggle = (m) => {
        const next = new Set(form.modules);
        if (next.has(m)) next.delete(m); else next.add(m);
        setForm({ ...form, modules: next });
    };

    const togglePerm = (k) => {
        const next = new Set(form.permissions);
        if (next.has(k)) next.delete(k); else next.add(k);
        setForm({ ...form, permissions: next });
    };

    const toggleCategoryAll = (cat, checked) => {
        const next = new Set(form.permissions);
        cat.permissions.forEach((p) => {
            if (checked) next.add(p.key); else next.delete(p.key);
        });
        setForm({ ...form, permissions: next });
    };

    const categoryAllState = (cat) => {
        const total = cat.permissions.length;
        const on = cat.permissions.filter((p) => form.permissions.has(p.key)).length;
        return on === 0 ? "none" : on === total ? "all" : "some";
    };

    const filteredCatalogue = permSearch
        ? catalogue.map((c) => ({
            ...c,
            permissions: c.permissions.filter((p) =>
                p.key.toLowerCase().includes(permSearch.toLowerCase()) ||
                (p.label_sq || "").toLowerCase().includes(permSearch.toLowerCase())
            ),
        })).filter((c) => c.permissions.length)
        : catalogue;

    const sensitiveSelected = catalogue
        .flatMap((c) => c.permissions)
        .filter((p) => p.sensitive && form.permissions.has(p.key)).length;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description || undefined,
            };
            if (mode === "modules") {
                payload.modules = Array.from(form.modules);
                payload.permissions = null;  // clear any fine-grained perms
            } else {
                payload.permissions = Array.from(form.permissions);
                payload.modules = [];
            }
            if (isEdit) {
                await api.patch(`/roles/${initial.id}`, payload);
                toast.success(t("staff.role_updated", { defaultValue: "Roli u përditësua" }));
            } else {
                await api.post("/roles", payload);
                toast.success(t("staff.role_created", { defaultValue: "Roli u krijua" }));
            }
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const disableSubmit = saving
        || (mode === "modules" && form.modules.size === 0)
        || (mode === "permissions" && form.permissions.size === 0);

    return (
        <Modal
            title={isEdit ? t("staff.edit_role", { defaultValue: "Edito rolin" }) : t("staff.add_role", { defaultValue: "Shto rol" })}
            onClose={onClose}
            width="max-w-2xl"
        >
            <form onSubmit={submit} className="space-y-3">
                <Field label={t("staff.role_name", { defaultValue: "Emri i rolit" })}>
                    <input className="akm-input" required minLength={2} maxLength={60} value={form.name}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder="p.sh. Menaxher Nate"
                           data-testid="role-name" />
                </Field>
                <Field label={t("staff.role_description", { defaultValue: "Përshkrimi" })}>
                    <input className="akm-input" value={form.description}
                           onChange={(e) => setForm({ ...form, description: e.target.value })}
                           placeholder={t("staff.role_description_hint", { defaultValue: "Për çfarë përdoret ky rol" })}
                           data-testid="role-description" />
                </Field>
                <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5" role="tablist">
                    <button type="button" onClick={() => setMode("modules")}
                            className={`rounded-[5px] px-3 py-1 text-[11.5px] font-medium ${mode === "modules" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                            data-testid="role-mode-modules">
                        {t("staff.by_modules", { defaultValue: "Sipas moduleve" })}
                    </button>
                    <button type="button" onClick={() => setMode("permissions")}
                            className={`rounded-[5px] px-3 py-1 text-[11.5px] font-medium ${mode === "permissions" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                            data-testid="role-mode-permissions">
                        {t("staff.by_permissions", { defaultValue: "Privilegje të hollësishme" })}
                    </button>
                </div>

                {mode === "modules" && (
                    <div>
                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("staff.role_modules", { defaultValue: "Modulet e lejuara" })}
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-border bg-muted/30 p-2">
                            {modules.map((m) => (
                                <label key={m} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[12px] hover:bg-background cursor-pointer" data-testid={`role-module-${m}`}>
                                    <input type="checkbox" checked={form.modules.has(m)} onChange={() => toggle(m)} className="h-3.5 w-3.5" style={{ width: "auto" }} />
                                    <span>{t(`staff.module.${m}`, { defaultValue: MODULE_LABEL[m] || m })}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
                {mode === "permissions" && (
                    <div>
                        <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            <span>{t("staff.fine_perms", { defaultValue: "Privilegjet e hollësishme" })}</span>
                            <span className="normal-case text-muted-foreground">
                                {form.permissions.size} {t("staff.perms_selected", { defaultValue: "të zgjedhura" })}
                                {sensitiveSelected > 0 && (
                                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                        ⚠ {sensitiveSelected} {t("staff.sensitive", { defaultValue: "sensitive" })}
                                    </span>
                                )}
                            </span>
                        </div>
                        <input className="akm-input mb-2" placeholder={t("staff.perm_search", { defaultValue: "Kërko privilegj…" })}
                               value={permSearch} onChange={(e) => setPermSearch(e.target.value)}
                               data-testid="role-perm-search" />
                        <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2">
                            {filteredCatalogue.map((cat) => {
                                const state = categoryAllState(cat);
                                return (
                                    <div key={cat.category} className="rounded-md bg-card p-1.5">
                                        <div className="flex items-center justify-between gap-2 border-b border-border pb-1 mb-1">
                                            <label className="flex items-center gap-2 text-[11.5px] font-semibold cursor-pointer">
                                                <input type="checkbox"
                                                       checked={state === "all"}
                                                       ref={(el) => { if (el) el.indeterminate = state === "some"; }}
                                                       onChange={() => toggleCategoryAll(cat, state !== "all")}
                                                       className="h-3.5 w-3.5" style={{ width: "auto" }}
                                                       data-testid={`role-cat-${cat.category}`} />
                                                <span className="uppercase tracking-wider">{cat.category}</span>
                                            </label>
                                        </div>
                                        <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                                            {cat.permissions.map((p) => (
                                                <label key={p.key} className="flex items-center gap-2 rounded px-1.5 py-0.5 text-[11.5px] hover:bg-muted/40 cursor-pointer"
                                                       data-testid={`role-perm-${p.key}`}>
                                                    <input type="checkbox" checked={form.permissions.has(p.key)}
                                                           onChange={() => togglePerm(p.key)} className="h-3.5 w-3.5" style={{ width: "auto" }} />
                                                    <span className={p.sensitive ? "text-amber-800 dark:text-amber-300" : ""}>
                                                        {p.label_sq || p.key}
                                                        {p.sensitive && <span className="ml-1 text-[9px]">⚠</span>}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredCatalogue.length === 0 && (
                                <div className="p-2 text-center text-[12px] text-muted-foreground">
                                    {t("staff.no_perms_found", { defaultValue: "Asnjë privilegj i gjetur." })}
                                </div>
                            )}
                        </div>
                        {sensitiveSelected > 0 && (
                            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200"
                                 data-testid="role-sensitive-warning">
                                ⚠ {t("staff.sensitive_warning", { defaultValue: "Ky rol përfshin privilegje sensitive. Kujdes gjatë caktimit të përdoruesve." })}
                            </div>
                        )}
                    </div>
                )}
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                    {t("staff.modules_hint", { defaultValue: "Zgjidhni modulet që përdoruesit me këtë rol mund t'i aksesojnë." })}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel", { defaultValue: "Anulo" })}</button>
                    <button type="submit" disabled={disableSubmit} className="akm-btn-primary" data-testid="role-save">
                        {saving ? t("common.saving", { defaultValue: "Duke ruajtur…" }) : t("common.save", { defaultValue: "Ruaj" })}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
